import React, { useState, useEffect } from "react";
import {
  Table,
  Form,
  OverlayTrigger,
  Tooltip,
  Dropdown,
  Pagination,
} from "react-bootstrap";
import Icon from "../generic/Icon";
import "./css/Audit.css";
import useApi from "../../apiProvider";
// import { use } from "react";

const Audit = () => {
  const [auditData, setAuditData] = useState([]);
  const [latestTimestamp, setLatestTimestamp] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAuditData = async () => {
      setIsLoading(true);
      try {
        const response = await useApi.getAuditData();
        const latestTimestamp = await useApi.getLatestTimestamp();
        console.log("API response:", response);
        console.log("Latest sync timestamp:", latestTimestamp);

        let audits = [];
        // The API returns the array directly
        if (Array.isArray(response)) {
          audits = response;
        } else if (Array.isArray(response?.returnData)) {
          audits = response.returnData;
        }

        if (Array.isArray(audits) && audits.length > 0) {
          setAuditData(audits.sort((a, b) => b.syncDate - a.syncDate));
        }

        if (latestTimestamp) {
          setLatestTimestamp(latestTimestamp);
        }
      } catch (error) {
        console.error("Error fetching audit data", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAuditData();
  }, []);

  const [sortConfig, setSortConfig] = useState({
    key: "syncDate",
    direction: "desc",
  });
  const [selectedOptions, setSelectedOptions] = useState({
    syncDate: { start: "", end: "" }, // Date range object
    entityId: [],
    name: [],
    auditType: [],
    action: [],
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  // const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Helper function to format Unix timestamp to readable date
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  // const formatDateTime = (timestamp) => {
  //   if (!timestamp) return "";
  //   const date = new Date(timestamp * 1000);
  //   return date.toLocaleString("en-US", {
  //     year: "numeric",
  //     month: "2-digit",
  //     day: "2-digit",
  //     hour: "2-digit",
  //     minute: "2-digit",
  //     second: "2-digit",
  //     hour12: true,
  //   });
  // };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedData = [...auditData].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setAuditData(sortedData);
  };

  // Filter and search data
  const filteredData = auditData.filter((item) => {
    // Filter by dropdowns
    const typeMatch =
      selectedOptions.auditType?.length === 0 ||
      selectedOptions.auditType?.includes(item.auditType);
    const actionMatch =
      selectedOptions.action?.length === 0 ||
      selectedOptions.action?.includes(item.action);

    // Date range filter with time normalization
    const dateMatch = (() => {
      if (!selectedOptions.syncDate.start && !selectedOptions.syncDate.end) {
        return true; // No date filter applied
      }
      const itemDate = new Date(item.syncDate * 1000);
      itemDate.setHours(0, 0, 0, 0); // Reset time to start of day

      const startDate = selectedOptions.syncDate.start
        ? new Date(selectedOptions.syncDate.start)
        : null;
      const endDate = selectedOptions.syncDate.end
        ? new Date(selectedOptions.syncDate.end)
        : null;

      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(23, 59, 59, 999); // End of day

      if (startDate && endDate) {
        return itemDate >= startDate && itemDate <= endDate;
      } else if (startDate) {
        return itemDate >= startDate;
      } else if (endDate) {
        return itemDate <= endDate;
      }
      return true;
    })();

    const idMatch =
      selectedOptions.entityId?.length === 0 ||
      selectedOptions.entityId?.includes(String(item.entityId));
    const nameMatch =
      selectedOptions.name?.length === 0 ||
      selectedOptions.name?.includes(item.name);

    // Global search
  //   const searchMatch =
  //     searchTerm === "" ||
  //     formatDate(item.syncDate)
  //       .toLowerCase()
  //       .includes(searchTerm.toLowerCase()) ||
  //     String(item.entityId).toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     item.auditType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     item.action?.toLowerCase().includes(searchTerm.toLowerCase());

    return (
      typeMatch &&
      actionMatch &&
      dateMatch &&
      idMatch &&
      nameMatch
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOptions, itemsPerPage]);

  const allFilterData = {
    syncDate: [
      ...new Set(
        auditData.map((item) => formatDate(item.syncDate)).filter(Boolean),
      ),
    ].map((date) => ({
      name: date,
    })),
    entityId: [
      ...new Set(
        auditData.map((item) => String(item.entityId)).filter(Boolean),
      ),
    ].map((id) => ({
      name: id,
    })),
    name: [...new Set(auditData.map((item) => item.name).filter(Boolean))].map(
      (name) => ({
        name: name,
      }),
    ),
    auditType: [
      ...new Set(auditData.map((item) => item.auditType).filter(Boolean)),
    ].map((type) => ({
      name: type,
    })),
    action: [
      ...new Set(auditData.map((item) => item.action).filter(Boolean)),
    ].map((action) => ({
      name: action,
    })),
  };

  return (
    <div
      style={{
        padding: "20px",
        height: "calc(100vh - 140px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          border: "2px solid #dee2e6",
          borderRadius: "8px",
          padding: "20px",
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "white",
          minHeight: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <h6 style={{ margin: 0 }}>CAFM Audit</h6>
            {!isLoading && (
              <span style={{ fontSize: "0.85rem", color: "#6c757d" }}>
                Time of Last Sync: {formatDate(latestTimestamp) || "N/A"}
              </span>
            )}
          </div>
          {/* Search - removed for now */}
          {/* {!isLoading && (
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <div style={{ position: "relative", width: "300px" }}>
                <Form.Control
                  type="text"
                  placeholder="Search all fields..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    paddingRight: searchTerm ? "2.2rem" : undefined,
                  }}
                />
                {searchTerm && (
                  <span
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      zIndex: 2,
                      color: "#888",
                    }}
                    onClick={() => setSearchTerm("")}
                  >
                    <Icon
                      name="close"
                      overrides={{
                        tooltip: "Clear search",
                        tooltipPlacement: "top",
                        style: { fontSize: "1.1rem", pointerEvents: "none" },
                        iconColor: "#E02827",
                      }}
                    />
                  </span>
                )}
              </div>
            </div>
          )} */}
        </div>

        {isLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <span
              className="spinner-border"
              role="status"
              aria-hidden="true"
            ></span>
            <span style={{ marginLeft: "10px" }}>Loading audit data...</span>
          </div>
        ) : (
          <>
            <div
              className="audit-table-container"
              style={{ flex: 1, overflow: "auto", minHeight: 0 }}
            >
              <Table striped bordered className="audit-table">
                <thead>
                  <tr>
                    <th>
                      <div>
                        <SortIcon
                          sortConfig={sortConfig}
                          columnKey="syncDate"
                          headerText="Sync Date"
                          onClick={() => handleSort("syncDate")}
                        />
                        <DateRangeDropDown
                          title={"syncDate"}
                          selectedOptions={selectedOptions}
                          setSelectedOptions={setSelectedOptions}
                          openDropdown={openDropdown}
                          setOpenDropdown={setOpenDropdown}
                          auditData={auditData}
                        />
                      </div>
                    </th>
                    <th>
                      <div>
                        <span>Type</span>
                        <MyDropDown
                          isDisabled={false}
                          title={"auditType"}
                          filterData={allFilterData?.auditType || []}
                          selectedOptions={selectedOptions}
                          setSelectedOptions={setSelectedOptions}
                          openDropdown={openDropdown}
                          setOpenDropdown={setOpenDropdown}
                        />
                      </div>
                    </th>
                    <th>
                      <div>
                        <SortIcon
                          sortConfig={sortConfig}
                          columnKey="entityId"
                          headerText="Site/Structure ID"
                          onClick={() => handleSort("entityId")}
                        />
                        <MyDropDown
                          isDisabled={false}
                          title={"entityId"}
                          filterData={allFilterData?.entityId || []}
                          selectedOptions={selectedOptions}
                          setSelectedOptions={setSelectedOptions}
                          openDropdown={openDropdown}
                          setOpenDropdown={setOpenDropdown}
                        />
                      </div>
                    </th>
                    <th>
                      <div>
                        <OverlayTrigger
                          placement="auto"
                          overlay={
                            <Tooltip id="name-tooltip">
                              Click to sort by Site/Structure Name
                            </Tooltip>
                          }
                        >
                          <SortIcon
                            sortConfig={sortConfig}
                            columnKey="name"
                            headerText="Site/Structure Name"
                            onClick={() => handleSort("name")}
                          />
                        </OverlayTrigger>
                        <MyDropDown
                          isDisabled={false}
                          title={"name"}
                          filterData={allFilterData?.name || []}
                          selectedOptions={selectedOptions}
                          setSelectedOptions={setSelectedOptions}
                          openDropdown={openDropdown}
                          setOpenDropdown={setOpenDropdown}
                        />
                      </div>
                    </th>
                    <th>
                      <div>
                        <span>Action</span>
                        <MyDropDown
                          isDisabled={false}
                          title={"action"}
                          filterData={allFilterData?.action || []}
                          selectedOptions={selectedOptions}
                          setSelectedOptions={setSelectedOptions}
                          openDropdown={openDropdown}
                          setOpenDropdown={setOpenDropdown}
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          textAlign: "center",
                          color: "#888",
                          padding: "40px 0",
                        }}
                      >
                        No results for given filter.
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((item) => (
                      <tr key={item.uniqueId}>
                        <td>{formatDate(item.syncDate)}</td>
                        <td>{item.auditType}</td>
                        <td>{item.entityId}</td>
                        <td>{item.name}</td>
                        <td>{item.action || "N/A"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination Controls */}
            {filteredData.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 12,
                  padding: "8px 0",
                  width: "100%",
                }}
              >
                {/* Entries Per Page */}
                <div>
                  Showing {indexOfFirstItem + 1} to{" "}
                  {Math.min(indexOfLastItem, filteredData.length)} of{" "}
                  {filteredData.length} entries
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    height: 40,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Form.Select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      style={{ width: 100, minHeight: 32, fontSize: "0.95rem" }}
                      size="sm"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </Form.Select>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Pagination size="sm" style={{ margin: 0 }}>
                      <Pagination.First
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      />
                      <Pagination.Prev
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      />
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        // Show first page, last page, current page, and pages around current
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 &&
                            pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <Pagination.Item
                              key={pageNumber}
                              active={pageNumber === currentPage}
                              onClick={() => setCurrentPage(pageNumber)}
                            >
                              {pageNumber}
                            </Pagination.Item>
                          );
                        } else if (
                          pageNumber === currentPage - 2 ||
                          pageNumber === currentPage + 2
                        ) {
                          return (
                            <Pagination.Ellipsis key={pageNumber} disabled />
                          );
                        }
                        return null;
                      })}
                      <Pagination.Next
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      />
                      <Pagination.Last
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      />
                    </Pagination>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const SortIcon = React.forwardRef(
  ({ sortConfig, columnKey, headerText, onClick, ...props }, ref) => {
    return (
      <span
        ref={ref}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          cursor: "pointer",
          minWidth: "60px",
        }}
        onClick={onClick}
        {...props}
      >
        {headerText}
        {sortConfig.key === columnKey && sortConfig.direction && (
          <Icon
            name={sortConfig.direction === "asc" ? "caretUp" : "caretDown"}
            overrides={{
              tooltip: `Sort ${
                sortConfig.direction === "asc" ? "Ascending" : "Descending"
              }`,
              tooltipPlacement: "top",
              style: {
                fontSize: "1rem",
                cursor: "pointer",
              },
            }}
            id={`sort-icon-${columnKey}`}
          />
        )}
      </span>
    );
  },
);

const MyDropDown = ({
  title,
  filterData = [],
  selectedOptions,
  setSelectedOptions,
  openDropdown,
  setOpenDropdown,
}) => {
  const [dropDownFilter, setDropDownFilter] = useState("");
  const isOpen = openDropdown === title;

  const displayedData = (filterData || []).filter((item) =>
    item.name?.toLowerCase().includes(dropDownFilter.toLowerCase()),
  );

  const isFilterActive = selectedOptions[title]?.length > 0;

  const handleCheckboxChange = (key, item) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [key]: prev[key]?.includes(item)
        ? prev[key].filter((i) => i !== item)
        : [...(prev[key] || []), item],
    }));
  };

  const handleClearFilters = () => {
    setSelectedOptions((prev) => ({
      ...prev,
      [title]: [],
    }));
  };

  return (
    <Dropdown
      align="end"
      show={isOpen}
      onToggle={(isOpen) => {
        if (isOpen) {
          setOpenDropdown(title);
        } else {
          setOpenDropdown(null);
        }
      }}
    >
      <Dropdown.Toggle
        className="no-caret"
        as="span"
        style={{
          cursor: "pointer",
          marginLeft: "5px",
          fontSize: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (isFilterActive) {
            // Clear filters if active
            handleClearFilters();
          } else {
            // Toggle dropdown if no filters are active
            setOpenDropdown(isOpen ? null : title);
          }
        }}
      >
        <Icon
          name={isFilterActive ? "removeFilter" : "filter"}
          overrides={{
            tooltip: isFilterActive ? "Clear filters" : "Filter table",
            tooltipPlacement: "top",
            iconColor: "#FFFFFF",
            style: {
              cursor: "pointer",
            },
          }}
          id={`filter-dropdown-${title}`}
        />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="dropdown-menu w-100 p-2"
        style={{
          maxHeight: "250px",
          overflow: "auto",
          zIndex: 1050,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Form.Control
          type="text"
          placeholder="Filter..."
          value={dropDownFilter}
          onChange={(e) => setDropDownFilter(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
        <hr className="my-1" />

        <Form.Check
          type="checkbox"
          label="Select All"
          checked={selectedOptions[title]?.length === filterData?.length}
          onChange={(e) => {
            e.stopPropagation();
            setSelectedOptions((prev) => ({
              ...prev,
              [title]:
                prev[title]?.length === filterData?.length
                  ? []
                  : (filterData || []).map((s) => s.name),
            }));
          }}
          className="px-3 ms-1 fw-bold"
        />
        <hr className="my-1" />

        {displayedData.map((item, index) => (
          <Form.Check
            key={index}
            type="checkbox"
            label={item.name}
            checked={selectedOptions[title]?.includes(item.name) || false}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(title, item.name);
            }}
            className="px-3 ms-1"
          />
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const DateRangeDropDown = ({
  title,
  selectedOptions,
  setSelectedOptions,
  openDropdown,
  setOpenDropdown,
  auditData,
}) => {
  const isOpen = openDropdown === title;
  const [tempStartDate, setTempStartDate] = useState(
    selectedOptions[title]?.start || "",
  );
  const [tempEndDate, setTempEndDate] = useState(
    selectedOptions[title]?.end || "",
  );

  // Calculate min and max dates from audit data
  let minDate = "";
  let maxDate = "";
  if (auditData && auditData.length > 0) {
    const dates = auditData.map((item) => new Date(item.syncDate * 1000));
    const minDateObj = new Date(Math.min(...dates));
    const maxDateObj = new Date(Math.max(...dates));
    if (!isNaN(minDateObj.getTime())) {
      minDate = minDateObj.toISOString().split("T")[0];
    }
    if (!isNaN(maxDateObj.getTime())) {
      maxDate = maxDateObj.toISOString().split("T")[0];
    }
  }

  const isFilterActive =
    selectedOptions[title]?.start || selectedOptions[title]?.end;

  const handleApply = () => {
    setSelectedOptions((prev) => ({
      ...prev,
      [title]: {
        start: tempStartDate,
        end: tempEndDate,
      },
    }));
    setOpenDropdown(null);
  };

  const handleClearFilters = () => {
    setTempStartDate("");
    setTempEndDate("");
    setSelectedOptions((prev) => ({
      ...prev,
      [title]: { start: "", end: "" },
    }));
  };

  return (
    <Dropdown
      align="end"
      show={isOpen}
      onToggle={(isOpen) => {
        if (isOpen) {
          setOpenDropdown(title);
          // Reset temp values to current filter values when opening
          setTempStartDate(selectedOptions[title]?.start || "");
          setTempEndDate(selectedOptions[title]?.end || "");
        } else {
          setOpenDropdown(null);
        }
      }}
    >
      <Dropdown.Toggle
        className="no-caret"
        as="span"
        style={{
          cursor: "pointer",
          marginLeft: "5px",
          fontSize: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (isFilterActive) {
            handleClearFilters();
          } else {
            setOpenDropdown(isOpen ? null : title);
          }
        }}
      >
        <Icon
          name={isFilterActive ? "removeFilter" : "filter"}
          overrides={{
            tooltip: isFilterActive ? "Clear date filter" : "Filter by date",
            tooltipPlacement: "top",
            iconColor: "#FFFFFF",
            style: {
              cursor: "pointer",
            },
          }}
          id={`filter-dropdown-${title}`}
        />
      </Dropdown.Toggle>

      <Dropdown.Menu
        className="dropdown-menu p-3"
        style={{
          minWidth: "280px",
          zIndex: 1050,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: "10px" }}>
          <Form.Label style={{ fontSize: "0.9rem", fontWeight: "500" }}>
            Start Date
          </Form.Label>
          <Form.Control
            type="date"
            value={tempStartDate}
            min={minDate}
            max={maxDate}
            onChange={(e) => setTempStartDate(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <div style={{ marginBottom: "15px" }}>
          <Form.Label style={{ fontSize: "0.9rem", fontWeight: "500" }}>
            End Date
          </Form.Label>
          <Form.Control
            type="date"
            value={tempEndDate}
            min={minDate}
            max={maxDate}
            onChange={(e) => setTempEndDate(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              handleClearFilters();
              setOpenDropdown(null);
            }}
            style={{ flex: 1 }}
          >
            Clear
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleApply}
            style={{ flex: 1 }}
          >
            Apply
          </button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default Audit;
